import { isRunLive } from './claude-fleet.utils'
import type { FleetFailureGroup, FleetPendingDestination, FleetPendingItem, FleetScheduledWhere, FleetUpcoming, FleetWaitingItem } from './claude-fleet.queue.types'
import type { FleetRun, FleetRunRecording } from './claude-fleet.types'

import { formatCurrency } from '@/utils/dateUtils'
import { formatDurationMs } from '@/utils/format-duration'

/**
 * How the Claude Fleet page reads what is happening now and what comes next: a run's spend against its
 * budget, a laptop run's progress, a browser recording, the Up next list and the work waiting for a
 * laptop runner. Pure functions over the fleet's records; times are read in the viewer's own zone.
 */

export interface FleetRunSpend {
  /** Spent so far for a live run, the final cost for a finished one; null when the fleet cannot say. */
  spentUsd: number | null
  budgetUsd: number | null
  /** Share of the budget spent, 0 to 100; null without both numbers. */
  percent: number | null
  /** "$1.20 of $10", "$1.20" or "—". */
  label: string
  /** The label's two halves, for a narrow cell that breaks between them: "$1.20" or "—", and "of $10" or "". */
  amountLabel: string
  budgetLabel: string
}

export function runSpend(run: Pick<FleetRun, 'state' | 'costUsd' | 'costSoFarUsd' | 'budgetUsd'>): FleetRunSpend {
  const spent = isRunLive(run.state) ? run.costSoFarUsd : run.costUsd
  const spentUsd = typeof spent === 'number' && Number.isFinite(spent) ? spent : null
  const budgetUsd = typeof run.budgetUsd === 'number' && run.budgetUsd > 0 ? run.budgetUsd : null
  const percent = spentUsd !== null && budgetUsd !== null ? Math.min(100, (spentUsd / budgetUsd) * 100) : null
  const amountLabel = spentUsd === null ? '—' : formatCurrency(spentUsd)
  const budgetLabel = spentUsd !== null && budgetUsd !== null ? `of ${formatCurrency(budgetUsd)}` : ''
  const label = budgetLabel ? `${amountLabel} ${budgetLabel}` : amountLabel
  return { spentUsd, budgetUsd, percent, label, amountLabel, budgetLabel }
}

/** "0.3M tokens · Bash: Show the PR diff" for a laptop run that reported progress; null otherwise. */
export function laptopProgressText(run: Pick<FleetRun, 'latestStep' | 'tokensSoFar'>): string | null {
  const parts: string[] = []
  if (typeof run.tokensSoFar === 'number' && run.tokensSoFar > 0) parts.push(tokensText(run.tokensSoFar))
  if (run.latestStep?.trim()) parts.push(run.latestStep.trim())
  return parts.length ? parts.join(' · ') : null
}

function tokensText(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M tokens`
  if (tokens >= 1_000) return `${Math.round(tokens / 1_000)}k tokens`
  return `${tokens} tokens`
}

/** "Kept until Nov 10" for a recording that still exists; null once it is past its deletion date. */
export function recordingKeptText(recording: FleetRunRecording | undefined, now: number): string | null {
  if (!recording) return null
  if (!recording.expiresAt) return 'Kept 30 days'
  const expires = Date.parse(recording.expiresAt)
  if (!Number.isFinite(expires) || expires < now) return null
  return `Kept until ${new Date(expires).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
}

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/**
 * A short, honest "when" for a time either side of now: "now", "in 25m", "in 6h", "tomorrow 06:00",
 * "Mon 10:00", or "Nov 3 10:00" further out; "5m ago" and "3h ago" for the past.
 */
export function whenText(iso: string | undefined, now: number): string {
  const at = iso ? Date.parse(iso) : Number.NaN
  if (!Number.isFinite(at)) return '—'
  const delta = at - now
  if (Math.abs(delta) < MINUTE_MS) return 'now'
  if (delta < 0) return agoText(-delta)
  if (delta < HOUR_MS) return `in ${Math.round(delta / MINUTE_MS)}m`
  const when = new Date(at)
  const clock = when.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  if (sameDay(when, new Date(now)) && delta < 12 * HOUR_MS) return `in ${Math.round(delta / HOUR_MS)}h`
  if (sameDay(when, new Date(now + DAY_MS))) return `tomorrow ${clock}`
  if (delta < 6 * DAY_MS) return `${when.toLocaleDateString('en-US', { weekday: 'short' })} ${clock}`
  return `${when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${clock}`
}

function agoText(elapsedMs: number): string {
  if (elapsedMs < HOUR_MS) return `${Math.round(elapsedMs / MINUTE_MS)}m ago`
  if (elapsedMs < DAY_MS) return `${Math.round(elapsedMs / HOUR_MS)}h ago`
  return `${Math.round(elapsedMs / DAY_MS)}d ago`
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

export const FLEET_WHERE_LABELS: Record<FleetScheduledWhere, string> = {
  agentcore: 'AgentCore',
  'runners-first': 'Runners first',
  'runners-only': 'Runners only',
  gateway: 'Housekeeping (no Claude run)'
}

export const FLEET_DESTINATION_LABELS: Record<FleetPendingDestination, string> = {
  runner: 'A runner',
  agentcore: 'AgentCore',
  'runner-or-agentcore': 'A runner, else AgentCore',
  'runner-only': 'Runners only',
  'owner-only': "Its author's own machine, if they opted in"
}

/** When a pending item starts: after its quiet period, or as soon as something takes it. */
export function pendingWhenText(item: Pick<FleetPendingItem, 'eligibleAt' | 'queuedAt'>, now: number): string {
  if (item.eligibleAt && Date.parse(item.eligibleAt) > now) return `Starts ${whenText(item.eligibleAt, now)}, after its quiet period`
  if (item.queuedAt) return `Waiting since ${whenText(item.queuedAt, now)}`
  return 'Ready to start'
}

/**
 * One line for the reviews and fixes that wait for their authors' own machines, which the fleet never takes,
 * so they are not counted as fleet work waiting to start: "38 PR fixes wait for their authors' own machines…".
 */
export function ownerOnlySummary(items: Pick<FleetPendingItem, 'activity'>[]): string {
  if (!items.length) return ''
  const fixes = items.filter(item => item.activity === 'pr-fix').length
  const reviews = items.length - fixes
  const kinds = [fixes ? `${fixes} PR ${fixes === 1 ? 'fix' : 'fixes'}` : '', reviews ? `${reviews} PR ${reviews === 1 ? 'review' : 'reviews'}` : '']
    .filter(Boolean)
    .join(' and ')
  if (items.length === 1) return `${kinds} waits for its author's own machine, not the fleet: it runs there only if they opted in to Claude on their PRs.`
  return `${kinds} wait for their authors' own machines, not the fleet: each runs there only if its author opted in to Claude on their PRs.`
}

/** "TE-123, adx#901", or the item itself when the work named no ticket or PR. */
export function pendingSubject(item: Pick<FleetPendingItem, 'touched' | 'item'>): string {
  const named = [...(item.touched?.tickets ?? []), ...(item.touched?.pullRequests ?? [])]
  return named.length ? named.join(', ') : item.item ?? ''
}

/** "run-start: 2 waiting, 1 being started · mention: empty". */
export function queueDepthText(queues: FleetUpcoming['queues']): string {
  return Object.entries(queues)
    .map(([name, depth]) => {
      const parts = [depth.visible ? `${depth.visible} waiting` : '', depth.inFlight ? `${depth.inFlight} being started` : ''].filter(Boolean)
      return `${name}: ${parts.length ? parts.join(', ') : 'empty'}`
    })
    .join(' · ')
}

/** "Waited 12m · 3 tries · dropped in 23h": how long laptop-only work has waited and how long it has left. */
export function waitingLine(item: Pick<FleetWaitingItem, 'queuedAt' | 'attempts' | 'dropsAt'>, now: number): string {
  const waited = Date.parse(item.queuedAt)
  const drops = Date.parse(item.dropsAt)
  const parts = [Number.isFinite(waited) ? `Waited ${formatDurationMs(Math.max(0, now - waited))}` : 'Waiting']
  if (item.attempts > 0) parts.push(item.attempts === 1 ? '1 try' : `${item.attempts} tries`)
  if (Number.isFinite(drops)) parts.push(drops > now ? `dropped ${whenText(item.dropsAt, now)}` : 'about to be dropped')
  return parts.join(' · ')
}

export const FLEET_RUNNER_EVENT_LABELS: Record<string, string> = {
  'sso-renew': 'SSO',
  'sso-nudge': 'SSO',
  'vpn-nudge': 'VPN',
  'cap-hit': 'Cap',
  'fleet-paused': 'Paused',
  command: 'Action',
  'offer-taken': 'Took',
  'run-finished': 'Finished'
}

export function runnerEventLabel(kind: string): string {
  return FLEET_RUNNER_EVENT_LABELS[kind] ?? kind
}

/** The query a link to one run carries; the page opens that run's drawer from it. */
export function fleetRunLinkQuery(runId: string): Record<string, string> {
  return { run: runId }
}

/** The Claude Fleet page opens a run's drawer from `#runs/<runId>`. */
export function fleetRunPath(runId: string): string {
  return `/admin/tools/claude-fleet#runs/${encodeURIComponent(runId)}`
}

/** The query and tab a link to one laptop runner carries; the Runners tab opens its drawer from it. */
export function fleetRunnerLink(machine: string): { query: Record<string, string>; hash: string } {
  return { query: { runner: machine }, hash: '#runners' }
}

/** A runner machine id as the fleet names one, so a link's `?runner=` cannot carry anything else. */
export const FLEET_RUNNER_MACHINE = /^[A-Za-z0-9._-]{1,80}$/

/** The failures panel's words for each cause the fleet groups by. */
export const FLEET_FAILURE_LABELS: Record<string, string> = {
  stopped: 'Stopped by someone',
  budget: 'Hit their budget',
  decision: 'Need a decision',
  'handed-back': 'Handed back by a runner',
  environment: 'Environment problems (SSO, VPN, network)',
  error: 'Failed with an error'
}

export interface FleetFailureCause {
  code: string
  label: string
  count: number
  /** The fleet's sentences for this cause, for the chip's hover. */
  details: string[]
}

/** The fleet's groups merged by cause, most frequent first: one chip per cause. */
export function failureCauses(groups: FleetFailureGroup[]): FleetFailureCause[] {
  const byCode = new Map<string, FleetFailureCause>()
  for (const group of groups) {
    const cause = byCode.get(group.code) ?? { code: group.code, label: FLEET_FAILURE_LABELS[group.code] ?? group.text, count: 0, details: [] }
    cause.count += group.count
    if (group.text && !cause.details.includes(group.text)) cause.details.push(group.text)
    byCode.set(group.code, cause)
  }
  return [...byCode.values()].sort((a, b) => b.count - a.count)
}
